import React, { useMemo, useState, useEffect } from "react";
import Layout from "../Components/Layout";
import {
  Box,
  Typography,
  Paper,
  Button,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  CircularProgress,
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import {
  createTerm,
  updateTerm,
  getTermById,
  listTerms,
} from "../redux/actions/termsActions";
import { useLocation } from "react-router-dom";
import useCallOnce from "../hooks/useCallOnce";
import {
  ContentState,
  convertToRaw,
  EditorState,
  convertFromHTML,
} from "draft-js";
import { Editor } from "react-draft-wysiwyg";
import draftToHtml from "draftjs-to-html";
import "react-draft-wysiwyg/dist/react-draft-wysiwyg.css";

function useQuery() {
  const { search } = useLocation();
  return useMemo(() => new URLSearchParams(search), [search]);
}

function TermsAndConditions() {
  const dispatch = useDispatch();
  const query = useQuery();
  const editingId = query.get("id");

  const { create, update, detail, list } = useSelector((s) => s.terms);

  const [type, setType] = useState("term");
  const [editorState, setEditorState] = useState(() =>
    EditorState.createEmpty()
  );
  const [currentId, setCurrentId] = useState(null);

  // useEffect(() => {
  //   if (editingId) {
  //     dispatch(getTermById(editingId));
  //   }
  // }, [dispatch, editingId]);

  // Load list for selected type; avoid React 18 StrictMode double-invoke
  useCallOnce(
    `terms/list/${type}`,
    () => {
      dispatch(listTerms({ page: 1, limit: 1, type }));
    },
    [dispatch, type]
  );

  useEffect(() => {
    const payload = detail?.data?.data || detail?.data;
    if (payload?.text) {
      const blocksFromHTML = convertFromHTML(payload.text);
      const content = ContentState.createFromBlockArray(
        blocksFromHTML.contentBlocks,
        blocksFromHTML.entityMap
      );
      setEditorState(EditorState.createWithContent(content));
    }
    if (payload?.type) setType(payload.type);
  }, [detail]);

  // Populate editor from list (selected type) when available
  useEffect(() => {
    const items =
      (list?.data?.data || list?.data || []).items ||
      list?.data?.data ||
      list?.data ||
      [];
    // items could be array or object with items
    const first = Array.isArray(items) ? items[0] : null;
    if (first) {
      setCurrentId(first.id || first._id);
      if (first.text) {
        const blocksFromHTML = convertFromHTML(first.text);
        const content = ContentState.createFromBlockArray(
          blocksFromHTML.contentBlocks,
          blocksFromHTML.entityMap
        );
        setEditorState(EditorState.createWithContent(content));
      } else {
        setEditorState(EditorState.createEmpty());
      }
    } else {
      // No record -> clear current id and editor
      setCurrentId(null);
      setEditorState(EditorState.createEmpty());
    }
  }, [list]);

  const saving = create.loading || update.loading;
  const listLoading = list.loading;

  const handleSave = async () => {
    const html = draftToHtml(convertToRaw(editorState.getCurrentContent()));
    // Always update when we have an id; otherwise create then set id
    if (editingId || currentId) {
      await dispatch(
        updateTerm({ id: editingId || currentId, type, text: html })
      );
    } else {
      const res = await dispatch(updateTerm({ type, text: html }));
      const newId = res?.data?.data?.id || res?.data?.id || null;
      if (newId) setCurrentId(newId);
    }
  };

  return (
    <Layout>
      <Box sx={{ width: "100%", minWidth: 0 }}>
        {/* <Typography
          variant="h6"
          sx={{ fontWeight: 700, color: "#111827", mb: 2 }}
        >
          Terms & Conditions
        </Typography> */}
        <Typography variant="h5" sx={{ fontWeight: 700, mb: 3 }}>
          Terms & Conditions
        </Typography>

        <Paper sx={{ p: 2, border: "1px solid #e5e7eb" }}>
          <Box sx={{ mb: 2 }}>
            <FormControl size="small" sx={{ minWidth: 220 }}>
              <InputLabel id="type-label">Type</InputLabel>
              <Select
                labelId="type-label"
                label="Type"
                value={type}
                onChange={(e) => setType(e.target.value)}
              >
                <MenuItem value={"term"}>Terms</MenuItem>
                <MenuItem value={"privacy"}>Privacy</MenuItem>
                <MenuItem value={"guidelines"}>Guidelines</MenuItem>
                <MenuItem value={"faq"}>FAQ's</MenuItem>
              </Select>
            </FormControl>
          </Box>

          <Box
            sx={{
              border: "1px solid #e5e7eb",
              borderRadius: 1,
              minHeight: 300,
              overflow: "visible", // <— important
              position: "relative",
            }}
          >
            {listLoading && (
              <Box
                sx={{
                  position: "absolute",
                  inset: 0,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  bgcolor: "rgba(255,255,255,0.6)",
                  zIndex: 2,
                  borderRadius: 1,
                }}
              >
                <CircularProgress size={28} />
              </Box>
            )}
            <Editor
              editorState={editorState}
              onEditorStateChange={setEditorState}
              toolbarClassName="rdw-toolbar"
              wrapperClassName="rdw-wrapper"
              editorClassName="rdw-editor"
              editorStyle={{
                paddingLeft: 15,
                paddingRight: 0,
                paddingTop: 0,
                paddingBottom: 12,
                minHeight: 280,
              }}
              toolbar={{
                options: [
                  "inline",
                  "blockType",
                  "fontSize",
                  "fontFamily",
                  "list",
                  "textAlign",
                  "link",
                  "image",
                  "remove",
                  "history",
                ],
              }}
            />
          </Box>

          <Box sx={{ mt: 2, display: "flex", gap: 1 }}>
            <Button variant="contained" onClick={handleSave} disabled={saving}>
              {saving ? (
                <CircularProgress size={20} sx={{ color: "#fff" }} />
              ) : (
                "Save"
              )}
            </Button>
          </Box>
        </Paper>
      </Box>
    </Layout>
  );
}

export default TermsAndConditions;
